import { Link } from "react-router-dom";
import { BRAND } from "@/lib/seo";
import { ACTIVATION_URL } from "@/lib/links";

const PRIMARY_LINKS = [
  ["/medical-care", "Physician-Guided Care", "footer-medical-care-link"],
  ["/wellness-optimization", "Optimization Programs", "footer-wellness-link"],
  ["/technology", "Technology", "footer-technology-link"],
  ["/team", "Team", "footer-team-link"],
  ["/updates", "Updates", "footer-updates-link"],
  ["/academy", "Integrated Academy", "footer-academy-link"],
  ["/founder-story", "Founder's Story", "footer-founder-link"],
  ["/ai-agents", "AI Agents", "footer-ai-agents-link"],
  ["/medical-director", "Medical Director", "footer-medical-director-link"],
  ["/contact", "Contact", "footer-contact-link"],
];

const LEARN_LINKS = [
  ["/digital-twin", "Digital Twin"], ["/precision-medicine", "Precision Medicine"],
  ["/longevity-medicine", "Longevity Medicine"], ["/biological-age-testing", "Biological Age Testing"],
  ["/metabolic-health", "Metabolic Health"], ["/hormone-optimization", "Hormone Optimization"],
  ["/genetic-testing", "Genetic Testing"], ["/microbiome-testing", "Microbiome Testing"],
  ["/sample-analysis", "Sample Analysis", "footer-sample-analysis-link"],
  ["/evidence", "Evidence Library", "footer-evidence-link"],
];

const PLATFORM_LINKS = [
  ["https://iwmc-body-app.emergent.host", "Optimization Programs Portal ↗", "footer-app-programs"],
  ["https://community-health-map-1.emergent.host", "Wellness Community Directory ↗", "footer-app-community"],
];

const FooterHero = () => (
  <>
    <div className="eyebrow mb-4">Integrated Wellness</div>
    <div className="font-display text-2xl md:text-3xl font-bold tracking-tight text-foreground/90">
      <span className="text-primary">Integrated Clinical Intelligence</span> for Connected Health Optimization.
    </div>
    <p className="mt-6 max-w-2xl mx-auto text-sm md:text-base text-foreground/65 leading-relaxed">
      AI-powered technology still in development. It supports wellness and care decisions; it does not replace emergency care or your judgment.
    </p>
    <a href={ACTIVATION_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-digital-twin-cta" className="mt-8 iw-btn-red inline-flex items-center gap-2 px-7 py-3.5 text-[0.7rem]">
      Start Your Optimization Analysis <span aria-hidden>→</span>
    </a>
  </>
);

const FooterPrimaryLinks = () => (
  <div className="mt-10 flex flex-wrap items-center justify-center gap-6 md:gap-10 text-base md:text-lg font-display font-semibold tracking-tight">
    {PRIMARY_LINKS.map(([to, label, testId], index) => (
      <span key={to} className="contents">
        {index > 0 && <span aria-hidden className="text-primary/40">·</span>}
        <Link to={to} data-testid={testId} className="text-foreground/85 hover:text-primary transition-colors">{label}</Link>
      </span>
    ))}
  </div>
);

const FooterResourceLinks = () => (
  <div data-testid="footer-learn-links" className="mt-8 pt-8 border-t border-border/60">
    <div className="iw-overline mb-4">Learn</div>
    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 font-mono text-[0.68rem] uppercase tracking-[0.14em]">
      {LEARN_LINKS.map(([href, label, testId]) => (
        <a key={href} href={href} data-testid={testId || `footer-learn-${href.slice(1)}`} className={`${href === "/evidence" ? "text-accent font-semibold" : "text-foreground/65"} hover:text-primary transition-colors`}>{label}</a>
      ))}
    </div>
  </div>
);

const FooterPlatformLinks = () => (
  <div data-testid="footer-apps-links" className="mt-8 pt-8 border-t border-border/60">
    <div className="iw-overline mb-4">Live Platforms</div>
    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 font-mono text-[0.68rem] uppercase tracking-[0.14em]">
      {PLATFORM_LINKS.map(([href, label, testId]) => (
        <a key={href} href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className="text-foreground/65 hover:text-accent transition-colors">{label}</a>
      ))}
    </div>
  </div>
);

const FooterClinical = () => (
  <>
    <div data-testid="footer-clinical-oversight" className="mt-14 pt-10 border-t border-border max-w-3xl mx-auto text-left space-y-4 font-mono text-[0.7rem] md:text-[0.78rem] leading-relaxed tracking-wide text-foreground/65">
      <p>Medical services delivered by <strong className="text-foreground/85">{BRAND.medicalOrg}</strong>, under the clinical direction of <strong className="text-foreground/85">{BRAND.medicalDirector.name}</strong>, Medical Director. Licensed for telemedicine across the United States.</p>
      <p>Wellness coaching and lifestyle optimization services are non-medical and do not require a clinical diagnosis or prescription.</p>
    </div>
    <div className="mt-10 font-mono text-[0.65rem] uppercase tracking-[0.22em] text-muted-foreground/80">© {new Date().getFullYear()} Integrated Wellness · All rights reserved</div>
  </>
);

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="relative bg-card border-t border-border">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-20 text-center">
        <FooterHero />
        <FooterPrimaryLinks />
        <FooterResourceLinks />
        <FooterPlatformLinks />
        <FooterClinical />
      </div>
    </footer>
  );
}